/* eslint-disable max-classes-per-file */
/* eslint-disable no-prototype-builtins */
/* eslint-disable @typescript-eslint/no-explicit-any */
import IconFilters from '@assets/icons/filters.svg?react';
import {
 closestCenter,
 DndContext,
 MouseSensor,
 TouchSensor,
 useSensor,
 useSensors,
} from '@dnd-kit/core';
import {
 arrayMove,
 SortableContext,
 useSortable,
 verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
 Box,
 Checkbox,
 IconButton,
 Theme,
 Typography,
 useTheme,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { usePage } from '../../../contexts/usePageContext';
import {
 changeCurrentPreset,
 ColPresetType,
} from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { RootState } from '../../../redux/store';
import { StructureType } from '../../../types/types';
import { findStructure, getTranslation } from '../../../utils/dataTypes';
import { isSelected } from './utils';

const IGNORE_TAGS = ['BUTTON'];

// Block Dnd, if IGNORE_TAGS includes elements tag or element has data-no-dnd attribute
const customHandleEvent = (element: HTMLElement | null) => {
 let cur = element;

 while (cur) {
  if (IGNORE_TAGS.includes(cur.tagName) || cur.dataset.noDnd) {
   return false;
  }
  cur = cur.parentElement;
 }

 return true;
};

MouseSensor.activators = [
 {
  eventName: 'onMouseDown',
  handler: ({ nativeEvent: event }: MouseEvent) =>
   customHandleEvent(event.target as HTMLElement),
 },
];

TouchSensor.activators = [
 {
  eventName: 'onTouchStart',
  handler: ({ nativeEvent: event }: React.TouchEvent<Element>) =>
   customHandleEvent(event.target as HTMLElement),
 },
];

const ColModalCurrentCol = ({
 k,
 tempColumnsPreset,
 handleChange,
 toggleFilter,
 structure,
}: {
 k: GridColDef;
 tempColumnsPreset: ColPresetType | null;
 handleChange: (data: {
  columns?: ColPresetType['columns'];
  filters?: ColPresetType['filters'];
 }) => void;
 toggleFilter: (field: string) => void;
 structure: StructureType[];
}) => {
 const th = useTheme();
 const {
  attributes,
  listeners,
  setNodeRef,
  transform,
  transition,
  isDragging,
 } = useSortable({ id: k.field });
 const trans = getTranslation(structure, k.field);
 const hasFilter = tempColumnsPreset?.filters?.fields?.hasOwnProperty(k.field);

 const style = {
  transform: CSS.Transform.toString(transform),
  backgroundColor: isDragging ? '#eee' : 'inherit',
  boxShadow: isDragging ? '0px 4px 12px rgba(0, 0, 0, 0.2)' : 'none',
  cursor: 'move',
  transition,
 };

 const handleCheckboxChange = () => {
  let newFilters = Object.fromEntries(
   Object.entries(tempColumnsPreset?.filters?.fields || {}).filter(
    ([fieldName]) => fieldName !== k.field,
   ),
  );

  let newOperators = Object.fromEntries(
   Object.entries(tempColumnsPreset?.filters?.operators || {}).filter(
    ([fieldName]) => fieldName !== k.field,
   ),
  );

  if (findStructure(structure, k.field)?.Type === 'datetime') {
   newFilters = Object.fromEntries(
    Object.entries(newFilters || {}).filter(
     ([fieldName]) => !['DateField', 'DateFrom', 'DateTo'].includes(fieldName),
    ),
   );
   newOperators = Object.fromEntries(
    Object.entries(newOperators || {}).filter(
     ([fieldName]) => !['DateField', 'DateFrom', 'DateTo'].includes(fieldName),
    ),
   );
  }

  handleChange({
   columns: tempColumnsPreset?.columns.filter((c) => c.field !== k.field),
   filters: {
    ...tempColumnsPreset?.filters,
    operators: newOperators,
    fields: newFilters,
   },
  });
 };

 return (
  <Box
   ref={setNodeRef}
   style={style}
   {...attributes}
   {...listeners}
   key={`selected_current_${k.field}`}
   sx={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
   }}
  >
   <Box
    sx={{
     width: '100%',
     display: 'flex',
     alignItems: 'center',
     mb: '4px',
     '.MuiButtonBase-root.MuiCheckbox-root': { p: '6px' },
    }}
   >
    <Checkbox
     //  sx={{  }}
     checked={isSelected(k.field, tempColumnsPreset)}
     onChange={handleCheckboxChange}
    />
    <Typography>{trans}</Typography>
    <IconButton
     sx={{
      ml: 'auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      // '& path': {
      //  stroke: (theme: Theme) =>
      //   hasFilter ? theme.palette.kemek.blue : theme.palette.kemek.lightestGray,
      // },
     }}
     size="small"
     onClick={() => toggleFilter(k.field)}
    >
     <IconFilters
      style={{
       color: hasFilter ? th.palette.kemek.blue : th.palette.kemek.lightestGray,
      }}
     />
    </IconButton>
   </Box>
  </Box>
 );
};

const ColModalCurrent = () => {
 const { t } = useTranslation();
 const dispatch = useDispatch();
 const { pageName } = usePage();

 const structure = useSelector((state: RootState) => {
  const pageState = state[pageName as keyof RootState];
  return pageState && 'structure' in pageState ? pageState.structure : [];
 });

 const tempColumnsPreset = useSelector(
  (state: RootState) =>
   (state.clientConfiguration.tempColumnsPreset?.[pageName] as ColPresetType) ||
   null,
 );

 const handleChange = ({
  columns,
  filters,
 }: {
  columns?: ColPresetType['columns'];
  filters?: ColPresetType['filters'];
 }) => {
  dispatch(
   changeCurrentPreset({
    page: pageName,
    type: 'columns',
    temp: true,
    preset: {
     name: tempColumnsPreset?.name || 'something is wrong',
     columns: columns || tempColumnsPreset?.columns || [],
     filters: filters || tempColumnsPreset?.filters || undefined,
    },
   }),
  );
 };

 const toggleFilter = (field: string) => {
  const filterActive = tempColumnsPreset?.filters?.fields.hasOwnProperty(field);

  const newFilters = { ...tempColumnsPreset?.filters?.fields };
  const newOperators = { ...tempColumnsPreset?.filters?.operators };
  if (filterActive) {
   delete newFilters[field];
   delete newOperators[field];
  } else {
   newFilters[field] = '';
   newOperators[field] = '';
  }

  handleChange({
   filters: {
    fields: newFilters,
    operators: newOperators,
   },
  });
 };

 const handleDragEnd = (event: any) => {
  const { active, over } = event;

  if (!active || !over) return;

  if (active.id !== over.id) {
   const oldIndex =
    tempColumnsPreset?.columns.findIndex((item) => item?.field === active.id) ||
    0;
   const newIndex =
    tempColumnsPreset?.columns.findIndex((item) => item?.field === over.id) ||
    0;

   handleChange({
    columns: arrayMove(tempColumnsPreset?.columns || [], oldIndex, newIndex),
   });
  }
 };
 const sensors = useSensors(
  useSensor(MouseSensor, {
   activationConstraint: {
    distance: 2,
   },
  }),
  useSensor(TouchSensor, {
   activationConstraint: {
    delay: 250,
    tolerance: 5,
   },
  }),
 );
 return (
  <Box
   sx={{
    borderRadius: '4px',
    border: (theme: Theme) => `1px solid ${theme.palette.kemek.lightestGray}`,
    // width: 280,
    flex: 1,
    mr: { xs: 0, md: 1 },
    overflowY: 'auto',
   }}
  >
   <Box
    sx={{
     p: 1,
     display: 'flex',
     alignItems: 'center',
     //  justifyContent: 'space-between',
     borderBottom: (theme: Theme) =>
      `1px solid ${theme.palette.kemek.lightestGray}`,
     '.MuiButtonBase-root.MuiCheckbox-root': { p: '6px' },
    }}
   >
    <Checkbox
     checked={
      Array.isArray(tempColumnsPreset?.columns) &&
      tempColumnsPreset?.columns.length > 0
     }
     onChange={() => {
      handleChange({ columns: undefined, filters: undefined });
     }}
    />
    <span>{t('colModal.colsSelected')}</span>
   </Box>
   <Box sx={{ p: 1 }}>
    <DndContext
     sensors={sensors}
     collisionDetection={closestCenter}
     onDragEnd={handleDragEnd}
    >
     <SortableContext
      items={tempColumnsPreset?.columns?.map((k) => k.field) || []}
      strategy={verticalListSortingStrategy}
     >
      {tempColumnsPreset?.columns?.map((k: GridColDef) => (
       // if (!str) return null;

       <ColModalCurrentCol
        key={k.field}
        k={k}
        tempColumnsPreset={tempColumnsPreset}
        handleChange={handleChange}
        toggleFilter={toggleFilter}
        structure={structure}
       />
      ))}
     </SortableContext>
    </DndContext>
   </Box>
  </Box>
 );
};

export default ColModalCurrent;
